/** Coordinates in-flight work only. Completed responses are never cached. */
export class ReportRequestCoordinator<T> {
  private readonly entries = new Map<
    string,
    {
      controller: AbortController;
      users: number;
      promise: Promise<T>;
    }
  >();
  private running = 0;
  private readonly queue: Array<() => void> = [];
  constructor(private readonly limit = 4) {}
  get pending(): number {
    return this.entries.size;
  }

  run(
    key: string,
    execute: (signal: AbortSignal) => Promise<T>,
    signal?: AbortSignal
  ): Promise<T> {
    if (signal?.aborted) return Promise.reject(this.aborted());
    let entry = this.entries.get(key);
    if (!entry || entry.controller.signal.aborted) {
      const controller = new AbortController();
      const created = {
        controller,
        users: 0,
        promise: undefined as unknown as Promise<T>,
      };
      created.promise = new Promise<T>((resolve, reject) => {
        const start = () => {
          if (controller.signal.aborted) {
            reject(this.aborted());
            return;
          }
          this.running++;
          Promise.resolve()
            .then(() => execute(controller.signal))
            .then(resolve, reject)
            .finally(() => {
              this.running--;
              this.drain();
            });
        };
        controller.signal.addEventListener(
          'abort',
          () => {
            const index = this.queue.indexOf(start);
            if (index >= 0) {
              this.queue.splice(index, 1);
              reject(this.aborted());
            }
          },
          { once: true }
        );
        this.queue.push(start);
      }).finally(() => {
        if (this.entries.get(key) === created) this.entries.delete(key);
      });
      entry = created;
      this.entries.set(key, created);
    }
    const shared = entry;
    shared.users++;
    const result = new Promise<T>((resolve, reject) => {
      let done = false;
      const finish = (callback: () => void) => {
        if (done) return;
        done = true;
        signal?.removeEventListener('abort', cancel);
        shared.users--;
        if (!shared.users) shared.controller.abort();
        callback();
      };
      const cancel = () => finish(() => reject(this.aborted()));
      signal?.addEventListener('abort', cancel, { once: true });
      shared.promise.then(
        value => finish(() => resolve(value)),
        error => finish(() => reject(error))
      );
    });
    this.drain();
    return result;
  }
  private drain(): void {
    while (this.running < this.limit && this.queue.length)
      this.queue.shift()!();
  }
  private aborted(): DOMException {
    return new DOMException('Report request cancelled', 'AbortError');
  }
}
